import React, { useState, useRef } from 'react';
import { FileImage, Copy, Trash2, Upload, Download } from 'lucide-react';

const Base64FileEncoderTool = () => {
  const [file, setFile] = useState(null);
  const [base64, setBase64] = useState('');
  const [preview, setPreview] = useState(null);
  const [fileInfo, setFileInfo] = useState(null);
  const [mode, setMode] = useState('encode'); // encode, decode
  const [decodeInput, setDecodeInput] = useState('');
  const [copied, setCopied] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setFileInfo({
      name: selectedFile.name,
      size: formatFileSize(selectedFile.size),
      type: selectedFile.type || 'unknown'
    });

    // Read and convert to base64
    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target.result;
      setBase64(result);
      
      // Set preview for images
      if (selectedFile.type.startsWith('image/')) {
        setPreview(result);
      } else {
        setPreview(null);
      }
    };
    reader.readAsDataURL(selectedFile);
  };

  const decodeBase64 = () => {
    if (!decodeInput.trim()) return;

    try {
      // Extract base64 data and mime type
      const matches = decodeInput.match(/^data:([^;]+);base64,(.+)$/);
      let mimeType = 'application/octet-stream';
      let base64Data = decodeInput;

      if (matches) {
        mimeType = matches[1];
        base64Data = matches[2];
      }

      // Decode and create blob
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });

      // Set file info
      setFileInfo({
        name: `decoded-file${getExtension(mimeType)}`,
        size: formatFileSize(blob.size),
        type: mimeType
      });

      // Set preview for images
      if (mimeType.startsWith('image/')) {
        const url = URL.createObjectURL(blob);
        setPreview(url);
      } else {
        setPreview(null);
      }

      setFile(blob);
    } catch (e) {
      alert('Invalid base64 data');
    }
  };

  const getExtension = (mimeType) => {
    const extensions = {
      'image/png': '.png',
      'image/jpeg': '.jpg',
      'image/gif': '.gif',
      'image/webp': '.webp',
      'image/svg+xml': '.svg',
      'application/pdf': '.pdf',
      'text/plain': '.txt',
      'application/json': '.json'
    };
    return extensions[mimeType] || '';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(base64);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadFile = () => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileInfo?.name || 'decoded-file';
    a.click();
    URL.revokeObjectURL(url);
  };

  const clearAll = () => {
    setFile(null);
    setBase64('');
    setPreview(null);
    setFileInfo(null);
    setDecodeInput('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex gap-2">
        <button
          onClick={() => setMode('encode')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            mode === 'encode' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          Encode (File → Base64)
        </button>
        <button
          onClick={() => setMode('decode')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            mode === 'decode' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          Decode (Base64 → File)
        </button>
      </div>

      {mode === 'encode' && (
        <div className="space-y-4">
          <div
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-gray-600 rounded-lg p-8 text-center cursor-pointer hover:border-blue-500 transition-colors"
          >
            <Upload className="w-12 h-12 text-gray-500 mx-auto mb-3" />
            <p className="text-gray-400 mb-2">Click to upload or drag and drop</p>
            <p className="text-xs text-gray-500">Supports images, documents, and other files</p>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {fileInfo && (
            <div className="p-4 bg-gray-800 rounded-lg">
              <h3 className="text-sm font-medium text-gray-300 mb-3">File Information</h3>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <div className="text-gray-500">Name</div>
                  <div className="text-white truncate">{fileInfo.name}</div>
                </div>
                <div>
                  <div className="text-gray-500">Size</div>
                  <div className="text-white">{fileInfo.size}</div>
                </div>
                <div>
                  <div className="text-gray-500">Type</div>
                  <div className="text-white">{fileInfo.type}</div>
                </div>
              </div>
            </div>
          )}

          {preview && (
            <div className="p-4 bg-gray-800 rounded-lg">
              <h3 className="text-sm font-medium text-gray-300 mb-3">Preview</h3>
              <img src={preview} alt="Preview" className="max-w-full max-h-48 rounded" />
            </div>
          )}

          {base64 && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Base64 Output</label>
              <textarea
                value={base64}
                readOnly
                className="w-full h-32 bg-gray-800 border border-gray-700 rounded-lg p-4 text-green-400 font-mono text-xs"
              />
              <p className="text-xs text-gray-500 mt-1">
                Length: {base64.length.toLocaleString()} characters
              </p>
            </div>
          )}
        </div>
      )}

      {mode === 'decode' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Base64 Input</label>
            <textarea
              value={decodeInput}
              onChange={(e) => setDecodeInput(e.target.value)}
              placeholder="data:image/png;base64,iVBORw0KGgo..."
              className="w-full h-32 bg-gray-800 border border-gray-700 rounded-lg p-4 text-white font-mono text-xs placeholder-gray-500 focus:border-blue-500"
            />
          </div>

          <button
            onClick={decodeBase64}
            disabled={!decodeInput.trim()}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
          >
            <FileImage size={18} />
            Decode Base64
          </button>

          {preview && mode === 'decode' && (
            <div className="p-4 bg-gray-800 rounded-lg">
              <h3 className="text-sm font-medium text-gray-300 mb-3">Decoded Preview</h3>
              <img src={preview} alt="Decoded" className="max-w-full max-h-48 rounded" />
            </div>
          )}
        </div>
      )}

      <div className="flex gap-3">
        {mode === 'encode' && (
          <button
            onClick={copyToClipboard}
            disabled={!base64}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
          >
            <Copy size={16} />
            {copied ? 'Copied!' : 'Copy Base64'}
          </button>
        )}
        {mode === 'decode' && file && (
          <button
            onClick={downloadFile}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
          >
            <Download size={16} />
            Download File
          </button>
        )}
        <button
          onClick={clearAll}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        >
          <Trash2 size={16} />
          Clear
        </button>
      </div>
    </div>
  );
};

export default Base64FileEncoderTool;
